import * as React from 'react'

import { clsx } from 'clsx'

import {
  Flex,
  Heading,
  PinIcon,
  SimpleSpinner,
  TentativeTimeIcon,
  Text,
  Badge,
  Tooltip,
  WarningIcon,
  RepeatIcon,
} from '@buffer-mono/popcorn'
import type { HeadingProps } from '@buffer-mono/popcorn'

import { PostingTime } from '~publish/components/PostingTime'
import type {
  NotificationStatus,
  PostStatus,
  SchedulingType,
} from '~publish/components/graphqlEnums'
import {
  isUnixEpochDate,
  isValidDateTime,
} from '~publish/helpers/dateFormatters'

import { PostNotifyBadge } from '../PostNotifyBadge'
import { ApprovalBadge, DraftBadge } from '../PostStatusBadge'

import styles from './PostingTimeline.module.css'

import type { JSX } from 'react'

/**
 * Component to display a timeline of posts.
 */
type PostingTimelineProps = React.ComponentPropsWithoutRef<'div'> & {
  as?: 'div' | 'main'
}

const PostingTimeline = React.forwardRef<HTMLDivElement, PostingTimelineProps>(
  ({ children, as: As = 'div', ...props }, ref) => {
    return (
      <As className={styles.timeline} ref={ref} {...props}>
        {children}
      </As>
    )
  },
)

PostingTimeline.displayName = 'PostingTimeline'

/**
 * Component to display a timeline entry, which is a container for a post with time to the side
 */
const PostingTimelineEntry = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<'div'>): JSX.Element => {
  return (
    <div className={styles.wrapper} {...props}>
      {children}
    </div>
  )
}

/**
 * Component to display a label for a post in the timeline, for any custom labels
 */
const PostingTimelineLabel = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => (
  <div className={styles.labelContainer}>
    <div className={styles.label}>{children}</div>
  </div>
)

const CustomScheduleLabel = (): JSX.Element => {
  return (
    <Tooltip content="Posting time was set manually and is not determined by the channel’s posting schedule">
      <Text
        className={styles.extraLabel}
        size="sm"
        color="subtle"
        icon={<PinIcon size="xsmall" />}
      >
        Custom
      </Text>
    </Tooltip>
  )
}

const TentativePostLabel = (): JSX.Element => {
  return (
    <Tooltip content="Once scheduled, this post will be published at this time">
      <Text
        className={styles.extraLabel}
        size="sm"
        color="subtle"
        icon={<TentativeTimeIcon size="xsmall" />}
      >
        Tentative
      </Text>
    </Tooltip>
  )
}

const TentativeOverduePostLabel = (): JSX.Element => {
  return (
    <Tooltip content="This post's tentative publish time has passed.">
      <Badge className={styles.extraLabel} variant="warning">
        <WarningIcon size="xsmall" /> Overdue
      </Badge>
    </Tooltip>
  )
}

const OverduePostLabel = (): JSX.Element => {
  return (
    <Tooltip content="This post is still processing and should publish shortly">
      <Badge className={styles.extraLabel} variant="warning">
        <WarningIcon size="xsmall" /> Sending...
      </Badge>
    </Tooltip>
  )
}

const RecurringPostLabel = ({
  tooltipContent,
}: {
  tooltipContent: string
}): JSX.Element => {
  return (
    <Tooltip content={tooltipContent}>
      <Text
        className={styles.extraLabel}
        size="sm"
        color="subtle"
        icon={<RepeatIcon size="xsmall" />}
      >
        Recurring
      </Text>
    </Tooltip>
  )
}

type PostingTimelineTimeLabelProps = React.ComponentPropsWithoutRef<'div'> & {
  date?: string
  includeDate?: boolean
  customScheduled?: boolean
  isPinned?: boolean
  overdue?: boolean
  tentative?: boolean
  disabled?: boolean
  status?: PostStatus
  notificationStatus?: NotificationStatus | null
  schedulingType?: SchedulingType | null
  recurring?: boolean
  recurrenceSummary?: string | null
}

const isDraft = (status?: PostStatus): boolean =>
  status === 'draft' || status === 'needs_approval'

const PostingTimelineSchedulingLabel = ({
  customScheduled = false,
  isPinned = false,
  overdue = false,
  tentative = false,
  status,
}: Pick<
  PostingTimelineTimeLabelProps,
  'customScheduled' | 'overdue' | 'tentative' | 'isPinned' | 'status'
>): JSX.Element | null => {
  if (overdue && (tentative || isDraft(status))) {
    return <TentativeOverduePostLabel />
  }
  if (tentative) {
    return <TentativePostLabel />
  }
  if (overdue) {
    return <OverduePostLabel />
  }
  if (customScheduled || isPinned) {
    return <CustomScheduleLabel />
  }
  return null
}

/**
 * Component to display a time label for a post in the timeline with specirfic labels for different states
 */
const PostingTimelineTimeLabel = ({
  date,
  includeDate = false,
  customScheduled = false,
  isPinned = false,
  overdue = false,
  tentative = false,
  disabled = false,
  recurring = false,
  status,
  notificationStatus,
  schedulingType,
  recurrenceSummary,
}: PostingTimelineTimeLabelProps): JSX.Element => {
  const hasTime = date && isValidDateTime(date) && !isUnixEpochDate(date)

  return (
    <div className={styles.labelContainer}>
      <Flex direction="column" gap="xs" className={styles.label}>
        {hasTime ? (
          <PostingTime date={date} includeDate={includeDate} muted={disabled} />
        ) : (
          <Text color="subtle" weight="medium">
            No time
          </Text>
        )}
        <PostingTimelineSchedulingLabel
          customScheduled={customScheduled}
          isPinned={isPinned}
          overdue={status !== 'sent' && overdue && !!hasTime}
          tentative={tentative}
          status={status}
        />
        {status === 'needs_approval' && <ApprovalBadge />}
        {status === 'draft' && <DraftBadge />}
        {recurring && (
          <RecurringPostLabel tooltipContent={recurrenceSummary ?? ''} />
        )}
        <PostNotifyBadge
          schedulingType={schedulingType}
          status={status}
          notificationStatus={notificationStatus}
        />
      </Flex>
    </div>
  )
}

/**
 * Component to display the content of timeline entry, like the post or slot
 */
const PostingTimelineContent = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => <div className={styles.content}>{children}</div>

const PostingTimelineHeader = ({
  children,
  as = 'h3',
}: {
  children: React.ReactNode
  as?: HeadingProps['as']
}): JSX.Element => {
  return (
    <Heading size="small" as={as} className={styles.header}>
      {children}
    </Heading>
  )
}

/**
 * Component to display a date header for a section of the timeline
 */
const PostingTimelineDateHeader = ({ date }: { date: string }): JSX.Element => {
  return (
    <Heading size="small" as="h3" className={styles.header}>
      {date}
    </Heading>
  )
}

type PostingTimelineLoadingProps = React.ComponentPropsWithoutRef<'div'> & {
  className?: string
}

/**
 * Component to display a loading state for the timeline, usually for infinite scroll UI
 */
const PostingTimelineLoading = React.forwardRef<
  HTMLDivElement,
  PostingTimelineLoadingProps
>(({ className, ...props }: PostingTimelineLoadingProps, ref) => {
  return (
    <div
      className={clsx(styles.loading, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This indeterminate timeline loader uses the progressbar role with the existing spinner presentation.
      role="progressbar"
      ref={ref}
      {...props}
    >
      <SimpleSpinner size="small" />
    </div>
  )
})

PostingTimelineLoading.displayName = 'PostingTimeline.Loading'

const PostingTimelineObject = Object.assign(PostingTimeline, {
  Entry: PostingTimelineEntry,
  Label: PostingTimelineLabel,
  TimeLabel: PostingTimelineTimeLabel,
  Content: PostingTimelineContent,
  Header: PostingTimelineHeader,
  Loading: PostingTimelineLoading,
  DateHeader: PostingTimelineDateHeader,
})

export { PostingTimelineObject as PostingTimeline }
